import { queryOptions, useQuery } from '@tanstack/react-query';

import type { DateRange } from '@cast/shared-types';
import type { DailyCostReportResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ORGANIZATION_CLUSTERS_DAILY_COSTS_QUERY_KEY =
  'organization-clusters-daily-costs';

type GetOrganizationClustersDailyCostsQueryKeyArgs = {
  from: { toISOString(): string };
  to: { toISOString(): string };
  useListingPrices: boolean;
};

const getOrganizationClustersDailyCostsQueryKey = ({
  from,
  to,
  useListingPrices,
}: GetOrganizationClustersDailyCostsQueryKeyArgs) =>
  [
    ORGANIZATION_CLUSTERS_DAILY_COSTS_QUERY_KEY,
    from.toISOString(),
    to.toISOString(),
    useListingPrices,
  ] as const;

type OrganizationClustersDailyCostsQuery = {
  range: DateRange;
  useListingPrices?: boolean;
  enabled?: boolean;
};

type UseOrganizationClustersDailyCostsQuery = {
  data?: DailyCostReportResponse;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  refetch: () => void;
};

const getOrganizationClustersDailyCostsQueryOptions = ({
  range: [from, to],
  useListingPrices = false,
  enabled = true,
}: OrganizationClustersDailyCostsQuery) =>
  queryOptions({
    queryKey: getOrganizationClustersDailyCostsQueryKey({
      from,
      to,
      useListingPrices,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getOrganizationClustersDailyCosts({
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          useListingPrices,
        });
      return data;
    },
    enabled,
  });

export const useOrganizationClustersDailyCostsQuery = (
  props: OrganizationClustersDailyCostsQuery
): UseOrganizationClustersDailyCostsQuery => {
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getOrganizationClustersDailyCostsQueryOptions(props)
  );
  return { data, isLoading, isFetching, error, refetch };
};
